import { useState } from 'react';
import { useScreener } from './useScreener';
import { getCompanyLogoUrl } from '../../config/dataProvider';
import Spinner from '../common/Spinner';
import InfoTip from '../common/InfoTip';
import SegmentedToggle from '../FunViz/components/SegmentedToggle';
import CollapsibleCard from '../FunViz/components/CollapsibleCard';
import CustomMetricsCard from '../FunViz/components/CustomMetricsCard';
import styles from './Screener.module.css';
// Single source of truth for which columns are FFO-basis for REITs (ADR 0014
// in funviz-amplify; byte-identical copy shared with funviz-amplify's own
// test fixtures). CustomMetricsCard's "REIT earnings basis" glossary entry
// already pins its copy to this same file — the per-cell marker below pins
// to it too, so the two can never drift apart.
import reitFfoGolden from '../FunViz/components/__tests__/fixtures/reit-ffo-golden.json';

// Static vocabulary for the sector filter. The API contract (spec Part 4)
// takes `f.sector` as free-form CSV against whatever the snapshot actually
// contains, but the frontend has no endpoint to discover that vocabulary
// from, so this is a fixed list rather than a live lookup. MUST match FMP's
// actual sector strings exactly (verified live against the production
// screener snapshot, 2026-11-01) — these are FMP's own taxonomy, NOT GICS.
// A prior version of this list used GICS-style names (e.g. "Information
// Technology", "Health Care", "Financials", "Consumer Discretionary",
// "Consumer Staples", "Materials") that never match any row, silently
// returning zero results for 6 of the 11 sectors.
const SECTOR_OPTIONS = [
  'Basic Materials',
  'Communication Services',
  'Consumer Cyclical',
  'Consumer Defensive',
  'Energy',
  'Financial Services',
  'Healthcare',
  'Industrials',
  'Real Estate',
  'Technology',
  'Utilities',
];

// Column catalogue for the results table. `key` matches the snapshot's
// metric keys (spec Part 5) so it can be passed straight through as the
// `sort` query param. `format` picks the cell formatter (see formatCell);
// every format except 'text' is numeric, which right-aligns the column and
// renders it with tabular figures (see .numeric in Screener.module.css).
// `tip` adds a short header InfoTip for columns whose meaning isn't obvious
// from the label alone.
//
// Some of these keys are newer than the deployed snapshot. A key the API
// doesn't return yet is simply `undefined` on the row and renders as "—",
// exactly like a not-computable `null`; sorting on it is still passed through
// unchanged (the backend sorts nulls last in both directions).
export const COLUMN_DEFS = {
  stock: { label: 'Stock', format: 'stock' },
  sector: { label: 'Sector', format: 'text' },
  industry: { label: 'Industry', format: 'text' },
  mcap: { label: 'Market Cap', format: 'currencyCompact' },
  volume: { label: 'Volume', format: 'compact' },

  // Valuation
  //
  // Naming (2026-11-01): the interpolated figure that used to be labeled
  // "Blended X" is now just "X" (it's the one most people want by default);
  // the plain last-reported-fiscal-year figure that used to be labeled "X"
  // is now "Trailing X", across every triad below (P/E, EV/EBITDA, EV/EBIT,
  // P/FCF, P/OCF, P/S, Dividend Yield). Field KEYS are unchanged (`peBlended`
  // is still "blended" internally) — only the display label moved.
  peBlended: {
    label: 'P/E',
    format: 'pe',
    tip: "Price ÷ EPS interpolated between the last reported fiscal year and next fiscal year's consensus. Doesn't include quarters reported since, so it can differ from the Analysis page's Key Facts P/E — usually within a few percent, but more after a large beat, miss, or one-off charge. Blank when the last fiscal year was a loss, even if trailing earnings have since turned positive.",
  },
  peReported: { label: 'Trailing P/E', format: 'pe' },
  fwdPe: { label: 'Fwd P/E', format: 'pe' },
  // "vs own N-year average" trio, one per valuation family (2026-11-01;
  // P/E's existed since 2026-10-30 as a bare multiple — now a signed
  // percent like its five new siblings below, and the old `pe5yMedian`/
  // `peVs5yMedian` median columns are gone: nothing outside the Screener
  // ever used median instead of average, so this trio already covers the
  // "rich/cheap vs its own history" job without a second, redundant stat).
  peVsAvg5y: {
    label: 'P/E vs 5Y Avg',
    format: 'pctSigned',
    tip: 'How far current P/E sits from its average fiscal-year-end P/E over the last 5 years — e.g. -12% is 12% cheaper than usual for this stock, +8% is 8% pricier. Loss years and P/Es above 150 are left out, and at least half the years must be usable.',
  },
  peVsAvg10y: {
    label: 'P/E vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/E vs 5Y Avg, over the last 10 years.',
  },
  peVsAvg15y: {
    label: 'P/E vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/E vs 5Y Avg, over the last 15 years.',
  },
  // EV/EBITDA and EV/EBIT triads (interpolated/trailing/Fwd — same shape as
  // the P/E triad above). EV/EBIT is a brand-new family (2026-11-01); its
  // Fwd and interpolated legs may come back null more often than
  // EV/EBITDA's, because EBIT consensus has thinner analyst coverage than
  // EBITDA's.
  evEbitdaBlended: {
    label: 'EV/EBITDA',
    format: 'times1',
    tip: "Enterprise value divided by EBITDA interpolated between the last reported fiscal year and next fiscal year's analyst consensus — the same blending P/E uses for earnings.",
  },
  evEbitda: { label: 'Trailing EV/EBITDA', format: 'times1' },
  evEbitdaFwd: {
    label: 'Fwd EV/EBITDA',
    format: 'times1',
    tip: "Enterprise value divided by next fiscal year's analyst consensus EBITDA.",
  },
  evEbitdaVsAvg5y: {
    label: 'EV/EBITDA vs 5Y Avg',
    format: 'pctSigned',
    tip: 'How far trailing EV/EBITDA sits from its average over the last 5 fiscal years. Each past year approximates EV as that year’s own FY-end price × shares, plus that year’s own net debt; values above 100× are left out, and at least half the years must be usable.',
  },
  evEbitdaVsAvg10y: {
    label: 'EV/EBITDA vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as EV/EBITDA vs 5Y Avg, over the last 10 years.',
  },
  evEbitdaVsAvg15y: {
    label: 'EV/EBITDA vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as EV/EBITDA vs 5Y Avg, over the last 15 years.',
  },
  evEbitBlended: {
    label: 'EV/EBIT',
    format: 'times1',
    tip: "Enterprise value divided by EBIT interpolated between the last reported fiscal year and next fiscal year's analyst consensus. Blank more often than the EV/EBITDA trio — EBIT consensus has thinner analyst coverage.",
  },
  evEbit: { label: 'Trailing EV/EBIT', format: 'times1' },
  evEbitFwd: {
    label: 'Fwd EV/EBIT',
    format: 'times1',
    tip: "Enterprise value divided by next fiscal year's analyst consensus EBIT. Blank more often than the EV/EBITDA trio — EBIT consensus has thinner analyst coverage.",
  },
  evEbitVsAvg5y: {
    label: 'EV/EBIT vs 5Y Avg',
    format: 'pctSigned',
    tip: 'Same as EV/EBITDA vs 5Y Avg, EBIT instead of EBITDA (ceiling 120×).',
  },
  evEbitVsAvg10y: {
    label: 'EV/EBIT vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as EV/EBIT vs 5Y Avg, over the last 10 years.',
  },
  evEbitVsAvg15y: {
    label: 'EV/EBIT vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as EV/EBIT vs 5Y Avg, over the last 15 years.',
  },
  // Cash-flow valuation cluster: the P/FCF triad, the two SBC-adjusted
  // P/FCF multiples, and the P/OCF triad all sit together here (and in the
  // Valuation view's own column list below) so every cash-flow-based
  // valuation ratio reads as one group instead of being scattered among the
  // P/E and PEG columns.
  pfcfBlended: {
    label: 'P/FCF',
    format: 'times1',
    tip: "Market cap divided by free cash flow interpolated between last fiscal year's reported figure and the same forward estimate Fwd P/FCF uses below.",
  },
  pfcf: { label: 'Trailing P/FCF', format: 'times1' },
  pfcfFwd: {
    label: 'Fwd P/FCF',
    format: 'times1',
    tip: "Market cap divided by next fiscal year's estimated free cash flow (analyst EBITDA × its historical median FCF/EBITDA) — unlike P/FCF after SBC below, not reduced by stock-based compensation.",
  },
  pfcfVsAvg5y: {
    label: 'P/FCF vs 5Y Avg',
    format: 'pctSigned',
    tip: 'How far trailing P/FCF sits from its average over the last 5 fiscal years. Each past year approximates market cap as that year’s own FY-end price × shares; values above 150× are left out, and at least half the years must be usable.',
  },
  pfcfVsAvg10y: {
    label: 'P/FCF vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/FCF vs 5Y Avg, over the last 10 years.',
  },
  pfcfVsAvg15y: {
    label: 'P/FCF vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/FCF vs 5Y Avg, over the last 15 years.',
  },
  peOwnerEarningsFcf: {
    label: 'P/FCF after SBC',
    format: 'times1',
    tip: 'Market cap divided by last fiscal year’s free cash flow minus stock-based compensation.',
  },
  peOwnerEarningsFcfForward: {
    label: 'Fwd P/FCF after SBC',
    format: 'times1',
    tip: 'Market cap divided by next fiscal year’s estimated free cash flow (analyst EBITDA × its 5-year median FCF/EBITDA) minus SBC (analyst revenue × last year’s SBC/revenue).',
  },
  // Renamed from "P/CF" (2026-10-31): the old label read as a plain
  // "price/cash flow" and was easy to mistake for a free-cash-flow multiple,
  // when it's actually priced off OPERATING cash flow. This comment used to
  // say a genuine SBC-unadjusted P/FCF didn't exist as a backend field at
  // all — that's no longer true as of 2026-11-01: see the `pfcf`/
  // `pfcfBlended`/`pfcfFwd` triad above, now shipped in the snapshot.
  pcfBlended: {
    label: 'P/OCF',
    format: 'times1',
    tip: "Market cap divided by operating cash flow interpolated between last fiscal year's reported figure and the same forward estimate Fwd P/OCF uses below.",
  },
  pcf: {
    label: 'Trailing P/OCF',
    format: 'times1',
    tip: 'Market cap divided by last fiscal year’s operating cash flow (before capex) — not free cash flow.',
  },
  pcfFwd: {
    label: 'Fwd P/OCF',
    format: 'times1',
    tip: "Market cap divided by next fiscal year's estimated operating cash flow (analyst EBITDA × its historical median OCF/EBITDA).",
  },
  pcfVsAvg5y: {
    label: 'P/OCF vs 5Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/FCF vs 5Y Avg, operating cash flow instead of free cash flow (ceiling 140×).',
  },
  pcfVsAvg10y: {
    label: 'P/OCF vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/OCF vs 5Y Avg, over the last 10 years.',
  },
  pcfVsAvg15y: {
    label: 'P/OCF vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/OCF vs 5Y Avg, over the last 15 years.',
  },
  peg: {
    label: 'PEG',
    format: 'plain2',
    tip: 'P/E divided by earnings growth, where growth is the average of the last 2 years’ and the next 3 years’ (analyst consensus) annual EPS growth. Around 1 or lower suggests the price is reasonable for the growth on offer. Blank when growth is zero or negative or no 3-year estimate exists.',
  },
  pegGraham: {
    label: 'Graham PEG',
    format: 'plain2',
    tip: 'P/E divided by (8.5 + 2 × the same growth rate as PEG), after Benjamin Graham’s fair-P/E formula; below 1 suggests the price is low for the growth.',
  },
  pb: { label: 'P/B', format: 'times1' },
  psBlended: {
    label: 'P/S',
    format: 'times1',
    tip: "Market cap divided by revenue interpolated between the last reported fiscal year and next fiscal year's analyst consensus — the same blending P/E uses for earnings.",
  },
  ps: { label: 'Trailing P/S', format: 'times1' },
  psFwd: {
    label: 'Fwd P/S',
    format: 'times1',
    tip: "Market cap divided by next fiscal year's analyst consensus revenue.",
  },
  psVsAvg5y: {
    label: 'P/S vs 5Y Avg',
    format: 'pctSigned',
    tip: 'How far trailing P/S sits from its average over the last 5 fiscal years; values above 30× are left out, and at least half the years must be usable.',
  },
  psVsAvg10y: {
    label: 'P/S vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/S vs 5Y Avg, over the last 10 years.',
  },
  psVsAvg15y: {
    label: 'P/S vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as P/S vs 5Y Avg, over the last 15 years.',
  },

  // Quality
  // Quality/Growth: the same 0-100 "Scores" card numbers as the Analysis
  // page (houseScores.mjs on the backend — see
  // funviz-amplify/docs/architecture/screener-architecture.md). Distinct
  // from — and not to be confused with — `epsStab`/`payoffYears`, which are
  // this screener's own older Aktienfinder-style metrics.
  qualityScore: {
    label: 'Quality Score',
    format: 'score100',
    tip: 'Our own 0–100 composite built to pick out high-quality compounders: profitability, dilution, leverage, free-cash-flow quality, revenue quality and consistency (earnings per share rising over five years on steady operating margins), with consistency weighted most. Leverage is equity to assets for banks, insurers and other financials and net debt to EBITDA for REITs; for banks and insurers, return on assets and net income also replace margin, free cash flow and revenue, and there is no consistency part. See "How FunViz’s own metrics are built" below for the full breakdown. It measures accounting stability and looks backward: for banks and insurers, the main risks (balance-sheet leverage, liquidity and tail events) are not fully visible in it.',
  },
  qualityScore5y: {
    label: 'Quality Score (5Y Avg)',
    format: 'score100',
    tip: 'The same 0–100 Quality composite, averaged over the trailing 5 fiscal years rather than the latest one — smooths out a single unusually strong or weak year.',
  },
  qualityScore10y: {
    label: 'Quality Score (10Y Avg)',
    format: 'score100',
    tip: 'The same 0–100 Quality composite, averaged over the trailing 10 fiscal years rather than the latest one — smooths out a single unusually strong or weak year.',
  },
  roic: { label: 'ROIC', format: 'pct1' },
  roe: { label: 'ROE', format: 'pct1' },
  roa: { label: 'ROA', format: 'pct1' },
  grossMargin: { label: 'Gross Margin', format: 'pct1' },
  opMargin: { label: 'Operating Margin', format: 'pct1' },
  netMargin: { label: 'Net Margin', format: 'pct1' },
  fcfConversion: {
    label: 'FCF Conversion',
    format: 'pct1',
    tip: 'Free cash flow as a share of net income. Around 100% or more means reported profits are turning into real cash.',
  },
  epsStab: { label: 'Earnings Stability', format: 'stab' },
  revStab: { label: 'Revenue Stability', format: 'stab' },
  cfStab: { label: 'Cash Flow Stability', format: 'stab' },

  // Debt
  netDebtEbitda: { label: 'Net Debt/EBITDA', format: 'times1' },
  interestCoverage: { label: 'Interest Coverage', format: 'times1' },
  debtEquity: { label: 'Debt/Equity', format: 'times2' },
  currentRatio: { label: 'Current Ratio', format: 'times2' },
  altmanZ: {
    label: 'Altman Z',
    format: 'plain2',
    tip: 'Bankruptcy-risk score built from profitability, leverage, liquidity and turnover. Above 3 is considered safe; below 1.8 signals financial distress.',
  },
  debtRatioTotal: { label: 'Debt Ratio', format: 'pct1' },
  payoffYears: { label: 'Payoff Years', format: 'years1' },

  // Dividends
  // Blended/Fwd siblings: the same dividend-history forecast the Analysis
  // page plots (funviz-amplify ADR 0013), regular dividends only — null for
  // companies that paid no regular dividend in the last twelve months.
  divYieldBlended: {
    label: 'Dividend Yield',
    format: 'pct2',
    tip: "Regular dividends paid over the last twelve months, divided by price — the same figure as the Analysis page's Key Facts dividend yield. Special dividends are left out (the trailing yield includes them). Blank wherever the forward figure is.",
  },
  divYield: { label: 'Trailing Dividend Yield', format: 'pct2' },
  divYieldFwd: {
    label: 'Fwd Dividend Yield',
    format: 'pct2',
    tip: "Next fiscal year's projected regular dividend divided by price — projected from the company's own dividend history (not analyst data), the same point the Analysis page's dividend chart shows. Blank for companies with no regular dividend in the last twelve months.",
  },
  divYieldVsAvg5y: {
    label: 'Dividend Yield vs 5Y Avg',
    format: 'pctSigned',
    tip: 'How far trailing dividend yield sits from its average over the last 5 fiscal years — unlike the other vs-Avg columns this has no ceiling (a high yield isn’t a denominator-near-zero artifact the way an extreme multiple is). At least half the years must have a usable dividend figure.',
  },
  divYieldVsAvg10y: {
    label: 'Dividend Yield vs 10Y Avg',
    format: 'pctSigned',
    tip: 'Same as Dividend Yield vs 5Y Avg, over the last 10 years.',
  },
  divYieldVsAvg15y: {
    label: 'Dividend Yield vs 15Y Avg',
    format: 'pctSigned',
    tip: 'Same as Dividend Yield vs 5Y Avg, over the last 15 years.',
  },
  divCagr5: { label: 'Dividend CAGR 5Y', format: 'pct1' },
  divCagr10: { label: 'Dividend CAGR 10Y', format: 'pct1' },
  divStreakYears: { label: 'Dividend Streak', format: 'int' },
  divNoCutYears: { label: 'Years Without Cut', format: 'int' },
  payoutEps: { label: 'Payout (EPS)', format: 'pct1' },
  payoutFcf: { label: 'Payout (FCF)', format: 'pct1' },
  shareholderYield: {
    label: 'Shareholder Yield',
    format: 'pct2',
    tip: 'Dividends plus net share buybacks, as a percentage of market cap. This is the two-factor version: it omits debt paydown, so it reads lower than sources using the common three-factor definition. A net share issuer can read below its dividend yield.',
  },
  styleScoreYield: {
    label: 'Yield Score',
    format: 'score100',
    warn: true,
    tip: 'Income-strategy score, 0–100. Weights the current dividend yield most heavily, plus earnings and dividend stability.',
  },
  styleScoreDivGrowth: {
    label: 'Div Growth Score',
    format: 'score100',
    warn: true,
    tip: 'Dividend-growth-strategy score, 0–100. Weights dividend growth most heavily, with a lower yield bar than the Yield Score.',
  },
  styleScoreEpsGrowth: {
    label: 'EPS Growth Score',
    format: 'score100',
    warn: true,
    tip: 'Earnings-growth-strategy score, 0–100. Weights earnings growth most heavily and ignores dividends.',
  },

  // Growth
  growthScoreTotal: {
    label: 'Growth Score (Total)',
    format: 'score100',
    tip: 'Growth score, 0–100: each year adds revenue growth to three-quarters of that year’s EBITDA margin (margin counted up to 40%; operating cash flow for REITs), and 70% or more scores 100. Below 15% revenue growth the score is capped (5% → at most 33, 10% → at most 67, below or falling growth → 0); only growth above the 5-year revenue peak counts (a recovery toward it scores 0), and growth counts less when revenue fell in more than 20% of up to the last 10 years (none above 60%). Total uses the lower of total and per-share revenue growth, so growth paid for with new shares does not count. Banks and other lenders (financials whose revenue is mostly interest income) are scored on revenue net of interest expense, with no margin credit. Shown as the average of the last 3 fiscal years.',
  },
  growthScoreTotal5y: {
    label: 'Growth Score (Total, 5Y Avg)',
    format: 'score100',
    tip: 'The same growth score averaged over the last 5 fiscal years instead of the headline’s 3, so one unusual year counts for less. Total uses the lower of total and per-share revenue growth, so growth paid for with new shares does not count.',
  },
  growthScoreTotal10y: {
    label: 'Growth Score (Total, 10Y Avg)',
    format: 'score100',
    tip: 'The same growth score averaged over the last 10 fiscal years instead of the headline’s 3, so one unusual year counts for less. Total uses the lower of total and per-share revenue growth, so growth paid for with new shares does not count.',
  },
  growthScorePerShare: {
    label: 'Growth Score (Per Share)',
    format: 'score100',
    tip: 'Growth score, 0–100: each year adds revenue growth to three-quarters of that year’s EBITDA margin (margin counted up to 40%; operating cash flow for REITs), and 70% or more scores 100. Below 15% revenue growth the score is capped (5% → at most 33, 10% → at most 67, below or falling growth → 0); only growth above the 5-year revenue peak counts (a recovery toward it scores 0), and growth counts less when revenue fell in more than 20% of up to the last 10 years (none above 60%). Per Share uses revenue per share, so buybacks lift it and dilution lowers it; it is never below Total. Banks and other lenders (financials whose revenue is mostly interest income) are scored on revenue net of interest expense, with no margin credit. Shown as the average of the last 3 fiscal years.',
  },
  growthScorePerShare5y: {
    label: 'Growth Score (Per Share, 5Y Avg)',
    format: 'score100',
    tip: 'The same per-share growth score averaged over the last 5 fiscal years instead of the headline’s 3, so one unusual year counts for less. Per Share uses revenue per share, so buybacks lift it and dilution lowers it; it is never below Total.',
  },
  growthScorePerShare10y: {
    label: 'Growth Score (Per Share, 10Y Avg)',
    format: 'score100',
    tip: 'The same per-share growth score averaged over the last 10 fiscal years instead of the headline’s 3, so one unusual year counts for less. Per Share uses revenue per share, so buybacks lift it and dilution lowers it; it is never below Total.',
  },
  // 10Y CAGR siblings added 2026-10-31, next to their 5Y counterparts (same
  // `cagr()` computation, just a longer window — already served by the
  // snapshot as `*Cagr10` but previously never surfaced here), matching the
  // 5Y/10Y-pair pattern the growth/quality SCORE columns already follow
  // above. `cfCagrEst`/`divCagrEst` are NOT added: the backend hardcodes
  // both to `null` (no FCF/OCF or dividend field in FMP's analyst-estimates
  // dataset on any tier — see funviz-amplify `screener-compute/index.mjs`),
  // so they'd only ever render "—".
  revCagr5: { label: 'Revenue CAGR 5Y', format: 'pct1' },
  revCagr10: { label: 'Revenue CAGR 10Y', format: 'pct1' },
  epsCagr5: { label: 'EPS CAGR 5Y', format: 'pct1' },
  epsCagr10: { label: 'EPS CAGR 10Y', format: 'pct1' },
  cfCagr5: { label: 'Cash Flow CAGR 5Y', format: 'pct1' },
  cfCagr10: { label: 'Cash Flow CAGR 10Y', format: 'pct1' },
  revCagrEst: { label: 'Revenue CAGR (Est.)', format: 'pct1' },
  epsCagrEst: { label: 'EPS CAGR (Est.)', format: 'pct1' },
  shareChange1y: { label: 'Share Count 1Y', format: 'pctSigned' },
  sbcPctRevenue: { label: 'SBC % Revenue', format: 'pct1' },

  // Price & risk
  chg1y: { label: '1Y Change', format: 'pctSigned' },
  drawdown52w: { label: 'Drawdown 52W', format: 'pct1' },
  mom200: { label: 'vs 200-Day Avg', format: 'pctSigned' },
  beta: {
    label: 'Beta',
    format: 'plain2',
    tip: 'Market beta: how much this stock has historically moved relative to the broad market (1.00 = in line, above 1 = more volatile).',
  },
};

// Every column except the two that columnsForView always prepends. Derived
// from COLUMN_DEFS rather than listed by hand so the "All" view can't drift
// out of sync with new columns the way a second hardcoded list would.
const ALL_COLUMNS = Object.keys(COLUMN_DEFS).filter((key) => key !== 'stock' && key !== 'sector');

// Grouped column views. Stock and Sector lead every view (prepended in
// columnsForView), so each list here is only that view's own metrics.
export const VIEWS = [
  { value: 'overview', label: 'Overview', columns: ['industry', 'mcap', 'qualityScore', 'growthScoreTotal', 'peBlended', 'divYield', 'chg1y', 'volume'] },
  // The "vs N-Year Avg" trios for everything except P/E sit as one block at
  // the end (2026-11-01), rather than interspersed after each family's own
  // Fwd column — that keeps every existing triad/cluster grouping above
  // (EV/EBITDA, EV/EBIT, the cash-flow cluster, P/S) exactly as contiguous
  // as before this trio existed.
  {
    value: 'valuation', label: 'Valuation', columns: [
      'peBlended', 'peReported', 'fwdPe', 'peVsAvg5y', 'peVsAvg10y', 'peVsAvg15y',
      'evEbitdaBlended', 'evEbitda', 'evEbitdaFwd',
      'evEbitBlended', 'evEbit', 'evEbitFwd',
      'pfcfBlended', 'pfcf', 'pfcfFwd',
      'peOwnerEarningsFcf', 'peOwnerEarningsFcfForward',
      'pcfBlended', 'pcf', 'pcfFwd',
      'peg', 'pegGraham', 'pb',
      'psBlended', 'ps', 'psFwd',
      'evEbitdaVsAvg5y', 'evEbitdaVsAvg10y', 'evEbitdaVsAvg15y',
      'evEbitVsAvg5y', 'evEbitVsAvg10y', 'evEbitVsAvg15y',
      'pfcfVsAvg5y', 'pfcfVsAvg10y', 'pfcfVsAvg15y',
      'pcfVsAvg5y', 'pcfVsAvg10y', 'pcfVsAvg15y',
      'psVsAvg5y', 'psVsAvg10y', 'psVsAvg15y',
    ],
  },
  { value: 'quality', label: 'Quality', columns: ['qualityScore', 'qualityScore5y', 'qualityScore10y', 'roic', 'roe', 'roa', 'grossMargin', 'opMargin', 'netMargin', 'fcfConversion', 'epsStab', 'revStab', 'cfStab'] },
  { value: 'debt', label: 'Debt', columns: ['netDebtEbitda', 'interestCoverage', 'debtEquity', 'currentRatio', 'altmanZ', 'debtRatioTotal', 'payoffYears'] },
  { value: 'dividends', label: 'Dividends', columns: ['divYieldBlended', 'divYield', 'divYieldFwd', 'divYieldVsAvg5y', 'divYieldVsAvg10y', 'divYieldVsAvg15y', 'divCagr5', 'divCagr10', 'divStreakYears', 'divNoCutYears', 'payoutEps', 'payoutFcf', 'shareholderYield', 'styleScoreYield', 'styleScoreDivGrowth', 'styleScoreEpsGrowth'] },
  { value: 'growth', label: 'Growth', columns: ['growthScoreTotal', 'growthScoreTotal5y', 'growthScoreTotal10y', 'growthScorePerShare', 'growthScorePerShare5y', 'growthScorePerShare10y', 'revCagr5', 'revCagr10', 'epsCagr5', 'epsCagr10', 'cfCagr5', 'cfCagr10', 'revCagrEst', 'epsCagrEst', 'shareChange1y', 'sbcPctRevenue'] },
  { value: 'risk', label: 'Price & Risk', columns: ['chg1y', 'drawdown52w', 'mom200', 'beta'] },
  { value: 'all', label: 'All', columns: ALL_COLUMNS },
];

const DEFAULT_VIEW = 'overview';
export const VIEW_STORAGE_KEY = 'funviz.screener.view';

const columnsForView = (viewValue) => {
  const view = VIEWS.find((v) => v.value === viewValue) || VIEWS[0];
  return ['stock', 'sector', ...view.columns].map((key) => ({ key, ...COLUMN_DEFS[key] }));
};

// Persisted view selection, same best-effort localStorage idiom as
// CollapsibleCard: unknown/malformed values and unavailable storage fall back
// to the default view.
const readStoredView = () => {
  if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_VIEW;
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEWS.some((v) => v.value === raw) ? raw : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
};

const writeStoredView = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, value);
  } catch {
    /* localStorage unavailable (private mode / quota) — persistence is best-effort */
  }
};

// Range filters (keys must be in useScreener's RANGE_FILTER_KEYS). `primary`
// filters are always visible; the rest sit behind a "More filters"
// disclosure, grouped by the same themes as the column views, so the panel
// stays a single tidy row by default (and short on mobile). Percent filters
// take percent numbers (3 means 3%), matching the column units.
const FILTER_DEFS = [
  { key: 'divYield', label: 'Dividend Yield (%)', step: '0.1', primary: true },
  { key: 'peBlended', label: 'P/E', step: '0.1', primary: true },
  { key: 'qualityScore', label: 'Quality Score (0–100)', step: '1', min: '0', max: '100', primary: true },
  { key: 'growthScoreTotal', label: 'Growth Score (0–100)', step: '1', min: '0', max: '100', primary: true },
  { key: 'evEbitda', label: 'EV/EBITDA', step: '0.1', group: 'Valuation' },
  { key: 'roic', label: 'ROIC (%)', step: '0.1', group: 'Quality' },
  { key: 'roe', label: 'ROE (%)', step: '0.1', group: 'Quality' },
  { key: 'epsStab', label: 'Earnings Stability', step: '0.01', min: '-1', max: '1', group: 'Quality' },
  { key: 'netDebtEbitda', label: 'Net Debt/EBITDA', step: '0.1', group: 'Debt' },
  { key: 'interestCoverage', label: 'Interest Coverage', step: '0.1', group: 'Debt' },
  { key: 'payoffYears', label: 'Payoff Years', step: '0.1', min: '0', group: 'Debt' },
  { key: 'divCagr5', label: 'Dividend CAGR 5Y (%)', step: '0.1', group: 'Dividends' },
  { key: 'divStreakYears', label: 'Dividend Streak (Years)', step: '1', min: '0', group: 'Dividends' },
  { key: 'payoutFcf', label: 'Payout (FCF, %)', step: '1', group: 'Dividends' },
  { key: 'volume', label: 'Volume', step: '1', min: '0', group: 'Trading' },
];

const PRIMARY_FILTERS = FILTER_DEFS.filter((f) => f.primary);
const MORE_FILTER_GROUPS = ['Valuation', 'Quality', 'Debt', 'Dividends', 'Trading'].map((group) => ({
  group,
  filters: FILTER_DEFS.filter((f) => f.group === group),
}));

// Only shown on rows whose best style score is still >=60 (metrics.mjs's
// SCORE_WARNING_GATE) — the flag means "scores well, but something just
// broke", so its ABSENCE never means "nothing broke". Say the gate out loud,
// or a crashed stock with no marker reads as a stock that did not crash.
const SCORE_WARNING_TEXT = 'Score warning: this stock still scores 60 or above, yet its price fell >30% in 3 months or earnings are expected to fall >20%. Stocks already scoring below 60 never carry this marker.';

// The 4 columns that, for REIT rows only, hold FFO-per-share-basis numbers
// instead of EPS-basis numbers (see CustomMetricsCard's "REIT earnings
// basis" glossary entry for the full explanation). Read from the shared
// golden fixture rather than hardcoded, so this stays in sync with the
// backend (funviz-amplify) and the glossary copy automatically if the list
// ever changes.
const FFO_BASIS_FIELDS = new Set(reitFfoGolden.ffoBasisFields);

const FFO_BASIS_MARKER_TEXT = 'FFO-basis for REITs, not EPS-basis — see "REIT earnings basis" below.';

// Compact magnitude suffixes, largest-first so we pick the first that the
// value clears. The API returns RAW values (e.g. mcap 5092828565000 is $5.09
// trillion in absolute dollars, NOT millions), so these divide the raw number
// down to a short "5.09T" / "75.0B" / "300M" style string.
const COMPACT_TIERS = [
  { limit: 1e12, suffix: 'T' },
  { limit: 1e9, suffix: 'B' },
  { limit: 1e6, suffix: 'M' },
  { limit: 1e3, suffix: 'K' },
];

// Render `scaled` (a number already divided into its tier, so 0 < |scaled| <
// 1000) with 3 significant figures: 5.09, 75.0, 300. This keeps the width
// predictable across magnitudes and drops trailing precision that would only
// add noise for large numbers.
const toThreeSigFigs = (scaled) => {
  const abs = Math.abs(scaled);
  let decimals;
  if (abs >= 100) decimals = 0;
  else if (abs >= 10) decimals = 1;
  else decimals = 2;
  return scaled.toFixed(decimals);
};

// Turn a raw number into a compact "5.09T" style string. Values below 1000
// render plainly (rounded to a whole number) since there's no useful suffix.
// Pure and deterministic (no locale/ICU dependence) so it's safe to unit-test
// directly and behaves identically in jsdom and the browser.
export const formatCompactNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const abs = Math.abs(num);
  for (const { limit, suffix } of COMPACT_TIERS) {
    if (abs >= limit) {
      return `${toThreeSigFigs(num / limit)}${suffix}`;
    }
  }
  return String(Math.round(num));
};

// Same magnitude logic as formatCompactNumber but prefixed with a currency
// symbol, for dollar-denominated columns like Market Cap.
export const formatCompactCurrency = (value) => `$${formatCompactNumber(value)}`;

const formatPe = (pe) => (
  // Once a P/E crosses 100 the tenths place is just noise (and widens the
  // column), so drop the decimal at that point; keep 1 decimal below 100.
  // Same rule for every P/E basis (interpolated, trailing, forward).
  Math.abs(pe) >= 100 ? pe.toFixed(0) : pe.toFixed(1)
);

// `null` means "not computable" (spec §6.9 rule 2) and must render
// distinctly from a real value, never coerced to 0 or blank-looking. A key
// the snapshot doesn't carry yet (`undefined`) and any non-finite number
// render the same way, so a missing field can never surface as "NaN".
export const formatCell = (format, value) => {
  if (value === null || value === undefined) return '—';
  if (format === 'text') return value;
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  switch (format) {
    case 'currencyCompact':
      return formatCompactCurrency(num);
    case 'compact':
      return formatCompactNumber(num);
    case 'pct1':
      return `${num.toFixed(1)}%`;
    case 'pct2':
      return `${num.toFixed(2)}%`;
    case 'pctSigned':
      return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
    case 'pe':
      return formatPe(num);
    case 'times1':
      return `${num.toFixed(1)}×`;
    case 'times2':
      return `${num.toFixed(2)}×`;
    case 'plain2':
    case 'stab':
      return num.toFixed(2);
    case 'years1':
      return num.toFixed(1);
    case 'int':
      return num.toFixed(0);
    case 'score100':
      // Already a 0-100 score — a whole number is precise enough for a table
      // cell. TWO unrelated score families share this formatter and share
      // nothing but the axis: `qualityScore`/`growthScore*` are the house
      // scores, on the same scale AND the same pillar basis as the Analysis
      // page's Scores card; `styleScoreYield`/`styleScoreDivGrowth`/
      // `styleScoreEpsGrowth` measure fit to an investing STYLE (a weighted
      // blend of ~7 inputs, rescaled from a 0-10 original) and are a
      // different basis entirely. Do not read the formatter as a claim that
      // every column it touches is comparable with the Scores card.
      return num.toFixed(0);
    default:
      return value;
  }
};

const isNumeric = (column) => column.format !== 'stock' && column.format !== 'text';

const ariaSortFor = (columnKey, sort, dir) => {
  if (columnKey !== sort) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
};

// The Screener tab: a filter panel plus a sortable, paginated results table
// against GET ${REACT_APP_BACKEND_URL}/screener (see useScreener.js and
// dataService.fetchScreener). Row activation navigates to the Analysis tab
// pre-loaded with that row's symbol via a `?symbol=` query param — the
// minimal "select a symbol from the URL" mechanism Analysis.js adds for
// this purpose (see the comment on its initial-fetch effect); there was no
// pre-existing one to reuse.
const Screener = () => {
  const {
    filters, updateFilter,
    sort, dir, toggleSort,
    page, goToPage,
    status, total, totalPages, rows,
  } = useScreener();

  const [view, setView] = useState(readStoredView);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const columns = columnsForView(view);

  const changeView = (next) => {
    setView(next);
    writeStoredView(next);
  };

  // Count of filters set inside the collapsed "More filters" section, shown
  // on its toggle so an active-but-hidden filter is never invisible.
  const activeMoreFilters = FILTER_DEFS.filter((f) => !f.primary
    && (filters[`${f.key}Min`] !== '' || filters[`${f.key}Max`] !== '')).length;

  const renderRangeFilter = ({ key, label, step, min, max }) => {
    const id = `screener-filter-${key.toLowerCase()}`;
    return (
      <div className={styles.filterField} key={key}>
        <span className={styles.filterLegend}>{label}</span>
        <div className={styles.filterRow}>
          <label htmlFor={`${id}-min`}>Min</label>
          <input
            id={`${id}-min`}
            type="number"
            step={step}
            min={min}
            max={max}
            value={filters[`${key}Min`]}
            onChange={(e) => updateFilter(`${key}Min`, e.target.value)}
          />
          <label htmlFor={`${id}-max`}>Max</label>
          <input
            id={`${id}-max`}
            type="number"
            step={step}
            min={min}
            max={max}
            value={filters[`${key}Max`]}
            onChange={(e) => updateFilter(`${key}Max`, e.target.value)}
          />
        </div>
      </div>
    );
  };

  // Row activation opens the Analysis tab pre-loaded with the row's symbol in
  // a NEW browser tab, so the screener (with its filters/sort/page state) stays
  // put in the current tab for the user to keep working through results.
  // REITs open with OCF selected: GAAP earnings (depreciation-heavy) understate
  // a REIT's cash generation, so OCF is the more meaningful starting metric.
  const goToSymbol = (sym, isReit) => {
    const metricParam = isReit === true ? '&metric=ocf' : '';
    window.open(`/analysis?symbol=${encodeURIComponent(sym)}${metricParam}`, '_blank', 'noopener');
  };

  const isFirstPage = page <= 1;
  const isLastPage = totalPages === 0 || page >= totalPages;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Screener</h2>

      <form className={styles.filterForm} aria-label="Screener filters">
        <div className={styles.filterPanel}>
          {PRIMARY_FILTERS.map(renderRangeFilter)}

          <div className={styles.filterField}>
            <label className={styles.filterLegend} htmlFor="screener-filter-sector">Sector</label>
            <select
              id="screener-filter-sector"
              value={filters.sector}
              onChange={(e) => updateFilter('sector', e.target.value)}
            >
              <option value="">All sectors</option>
              {SECTOR_OPTIONS.map((sector) => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className={styles.moreFiltersToggle}
          aria-expanded={moreFiltersOpen}
          aria-controls="screener-more-filters"
          onClick={() => setMoreFiltersOpen((open) => !open)}
        >
          {moreFiltersOpen ? 'Fewer filters' : 'More filters'}
          {activeMoreFilters > 0 && ` (${activeMoreFilters} active)`}
        </button>

        {moreFiltersOpen && (
          <div id="screener-more-filters" className={styles.moreFilters}>
            {MORE_FILTER_GROUPS.map(({ group, filters: groupFilters }) => (
              <fieldset key={group} className={styles.filterGroup}>
                <legend className={styles.filterGroupLegend}>{group}</legend>
                <div className={styles.filterPanel}>
                  {groupFilters.map(renderRangeFilter)}
                </div>
              </fieldset>
            ))}
          </div>
        )}
      </form>

      {/* Column-group switcher. Only changes which columns are shown; the
          query (filters/sort/page) is untouched, so no refetch. */}
      <div className={styles.viewSwitcher}>
        <SegmentedToggle
          options={VIEWS}
          value={view}
          onChange={changeView}
          ariaLabel="Column view"
        />
      </div>

      {status === 'fetching' && (
        <div className={styles.statusMessage}>
          <Spinner label="Loading screener results…" />
        </div>
      )}

      {status === 'unavailable' && (
        <p className={styles.statusMessage} role="status">
          Screener data is still being prepared. Check back soon.
        </p>
      )}

      {status === 'error' && (
        <p className={styles.statusMessage} role="alert">
          Couldn't load screener results — please try again.
        </p>
      )}

      {status === 'fetched' && rows.length === 0 && (
        <p className={styles.statusMessage}>
          No stocks match the current filters.
        </p>
      )}

      {status === 'fetched' && rows.length > 0 && (
        <>
          {/* Universe-size summary lives ABOVE the table as its own paragraph
              (rather than inside a <caption>) so it reads as a standalone
              status line. role="status" keeps it announced to screen readers,
              which the removed <caption> previously handled. */}
          <p className={styles.resultsSummary} role="status">
            US common-stock screener results, {total} matching {total === 1 ? 'stock' : 'stocks'}
          </p>

          {/* Wraps the table so its many columns can scroll horizontally on
              narrow viewports instead of blowing out the page's width — see
              .tableScroll in Screener.module.css. This div has no ARIA role
              of its own, so screen.getByRole('table') still resolves to the
              <table> unaffected. */}
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {columns.map((column) => {
                    const sortKey = column.key === 'stock' ? 'sym' : column.key;
                    const numeric = isNumeric(column);
                    return (
                      <th
                        key={column.key}
                        scope="col"
                        className={numeric ? styles.numeric : undefined}
                        aria-sort={ariaSortFor(sortKey, sort, dir)}
                      >
                        <button
                          type="button"
                          className={styles.sortButton}
                          onClick={() => toggleSort(sortKey)}
                        >
                          {column.label}
                          {sort === sortKey && (dir === 'asc' ? ' ▲' : ' ▼')}
                        </button>
                        {column.tip && (
                          // Wrapper flips the bubble to open downward: an
                          // upward bubble would be clipped by .tableScroll's
                          // overflow above the header row.
                          <span className={styles.headerTip}>
                            <InfoTip text={column.tip} align={numeric ? 'right' : 'left'} />
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.sym} className={styles.row}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={isNumeric(column) ? styles.numeric : undefined}
                      >
                        {column.key === 'stock' ? (
                          <button
                            type="button"
                            className={styles.stockCell}
                            aria-label={`View analysis for ${row.sym}${row.isReit === true ? ' (REIT)' : ''}`}
                            onClick={() => goToSymbol(row.sym, row.isReit)}
                          >
                            {/* Company logo is gated behind data-provider
                                licensing/branding clearance (see
                                config/dataProvider.js). Returns null while
                                disabled — falls back to ticker + name, no
                                layout break, no hotlink to the provider's
                                asset host. */}
                            {getCompanyLogoUrl(row.sym) && (
                              <img
                                src={getCompanyLogoUrl(row.sym)}
                                alt=""
                                className={styles.stockLogo}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            )}
                            <span className={styles.stockIdentity}>
                              <span className={styles.stockTicker}>
                                {row.sym}
                                {row.isReit === true && (
                                  <span className={styles.reitTag}>REIT</span>
                                )}
                              </span>
                              <span className={styles.stockName}>{row.name}</span>
                            </span>
                          </button>
                        ) : (
                          <>
                            {column.warn && row.styleScoreWarning === true && (
                              <span
                                className={styles.styleScoreWarning}
                                role="img"
                                aria-label={SCORE_WARNING_TEXT}
                                title={SCORE_WARNING_TEXT}
                              >
                                ⚠
                              </span>
                            )}
                            {formatCell(column.format, row[column.key])}
                            {/* FFO-basis marker: only on the 4 columns
                                FFO_BASIS_FIELDS names, only for REIT rows,
                                and never on a null/"—" cell — same
                                null-or-undefined check formatCell uses
                                above, there's no number here to qualify. */}
                            {FFO_BASIS_FIELDS.has(column.key) && row.isReit === true
                              && row[column.key] !== null && row[column.key] !== undefined && (
                              <sup
                                className={styles.ffoBasisMarker}
                                role="img"
                                aria-label={FFO_BASIS_MARKER_TEXT}
                                title={FFO_BASIS_MARKER_TEXT}
                              >
                                †
                              </sup>
                            )}
                          </>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav className={styles.pagination} aria-label="Screener pagination">
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={isFirstPage}
            >
              First
            </button>
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={isFirstPage}
            >
              Previous
            </button>
            <span className={styles.pageStatus}>
              Page {page} of {Math.max(totalPages, 1)}
            </span>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={isLastPage}
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => goToPage(totalPages)}
              disabled={isLastPage}
            >
              Last
            </button>
          </nav>
        </>
      )}

      {/* Static documentation of FunViz's own proprietary metrics — renders
          unconditionally (outside the fetch conditional above), mirroring
          how DisclaimerFooter renders unconditionally at the bottom of
          Analysis.js. Collapsed by default, like most of Analysis's
          secondary CollapsibleCards. */}
      <CollapsibleCard
        title="How FunViz's own metrics are built"
        storageKey="funviz.screener.metricsGlossary"
        defaultOpen={false}
      >
        <CustomMetricsCard />
      </CollapsibleCard>
    </div>
  );
};

export default Screener;
